import { useTranslation } from 'react-i18next';
import { formatCents } from '@/domain/money';
import { cashFirstWaterfall } from '@/domain/collectionEngine';
import { formatDate, formatDateTime, formatShortDate } from '@/lib/dateUtils';
import type { Receipt } from '@/domain/types';

/**
 * The slip handed to the customer.
 *
 * The Cash column is derived with the same cash-first waterfall the printed
 * report uses, so the slip in the customer's hand and the sheet in the office
 * show the same figures against the same invoices. If they disagreed, the
 * customer would be right and the office would be wrong.
 */
export function ReceiptSlip({ receipt }: { receipt: Receipt }): JSX.Element {
  const { t } = useTranslation();

  const cheques = receipt.payments.filter((payment) => payment.method === 'CHEQUE');
  const cashPortions = cashFirstWaterfall(
    receipt.allocations.map((allocation) => ({
      invoiceId: allocation.invoiceId,
      amountCents: allocation.allocatedCents,
    })),
    receipt.cashCents,
  );

  return (
    <div className="print-sheet mx-auto max-w-2xl rounded-lg border border-slate-300 bg-white p-6">
      <header className="border-b border-slate-300 pb-3 text-center">
        <h2 className="text-lg font-bold tracking-wide text-brand-700">THARANGA STEEL</h2>
        <p className="text-xs text-slate-600">No. 526, Koswadiya, Mahawewa. · 032 20 59 447</p>
        <p className="mt-2 text-sm font-semibold">{t('collection.slip')}</p>
      </header>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 py-3 text-xs">
        <div className="flex gap-2">
          <dt className="font-semibold">{t('collection.receiptNo')}:</dt>
          <dd className="tabular font-bold">{receipt.receiptNo}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="font-semibold">{t('common.date')}:</dt>
          {/* System-generated visit timestamp, per the requirements. */}
          <dd>{formatDateTime(receipt.collectedAt)}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="font-semibold">{t('collection.customer')}:</dt>
          <dd>{receipt.shopName}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="font-semibold">{t('collection.agent')}:</dt>
          <dd>{receipt.agentName}</dd>
        </div>
      </dl>

      {/* Per-invoice assignment of the collected money. Scrolls internally on a
          narrow phone instead of forcing the whole page sideways. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[420px] border-collapse text-xs">
          <thead>
            <tr>
              <th className="border border-slate-400 px-2 py-1 text-left">
                {t('report.sheet.invoiceNo')}
              </th>
              <th className="border border-slate-400 px-2 py-1 text-right">
                {t('report.sheet.invoiceAmount')}
              </th>
              <th className="border border-slate-400 px-2 py-1 text-right">{t('collection.cash')}</th>
              <th className="border border-slate-400 px-2 py-1 text-right">{t('collection.cheque')}</th>
              <th className="border border-slate-400 px-2 py-1 text-right">
                {t('invoice.outstanding')}
              </th>
            </tr>
          </thead>
          <tbody>
            {receipt.allocations.map((allocation, index) => {
              const fromCash = cashPortions[index] ?? 0;
              const fromCheque = allocation.allocatedCents - fromCash;

              return (
                <tr key={allocation.invoiceId}>
                  <td className="border border-slate-400 px-2 py-1">{allocation.invoiceNo}</td>
                  <td className="tabular border border-slate-400 px-2 py-1 text-right">
                    {formatCents(allocation.invoiceAmountCents)}
                  </td>
                  <td className="tabular border border-slate-400 px-2 py-1 text-right">
                    {fromCash > 0 ? formatCents(fromCash) : ''}
                  </td>
                  <td className="tabular border border-slate-400 px-2 py-1 text-right">
                    {fromCheque > 0 ? formatCents(fromCheque) : ''}
                  </td>
                  <td className="tabular border border-slate-400 px-2 py-1 text-right">
                    {formatCents(allocation.outstandingAfterCents)}
                  </td>
                </tr>
              );
            })}

            <tr className="font-bold">
              <td className="border border-slate-400 px-2 py-1 text-right" colSpan={2}>
                {t('common.total')}
              </td>
              <td className="tabular border border-slate-400 px-2 py-1 text-right">
                {formatCents(receipt.cashCents)}
              </td>
              <td className="tabular border border-slate-400 px-2 py-1 text-right">
                {formatCents(receipt.chequeCents)}
              </td>
              <td className="border border-slate-400 px-2 py-1" />
            </tr>
          </tbody>
        </table>
      </div>

      {cheques.length > 0 && (
        <div className="mt-3">
          <p className="mb-1 text-xs font-semibold">{t('collection.cheque')}</p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[360px] border-collapse text-xs">
              <thead>
                <tr>
                  <th className="border border-slate-400 px-2 py-1 text-left">
                    {t('collection.chequeNo')}
                  </th>
                  <th className="border border-slate-400 px-2 py-1 text-left">
                    {t('collection.bank')}
                  </th>
                  <th className="border border-slate-400 px-2 py-1 text-left">
                    {t('collection.chequeDate')}
                  </th>
                  <th className="border border-slate-400 px-2 py-1 text-right">
                    {t('collection.amount')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {cheques.map((cheque) => (
                  <tr key={cheque.id}>
                    <td className="border border-slate-400 px-2 py-1">{cheque.chequeNo}</td>
                    <td className="border border-slate-400 px-2 py-1">
                      {cheque.bankShortCode ?? cheque.bankName}
                    </td>
                    <td className="border border-slate-400 px-2 py-1">
                      {cheque.chequeDate ? formatDate(cheque.chequeDate) : formatShortDate(null)}
                    </td>
                    <td className="tabular border border-slate-400 px-2 py-1 text-right">
                      {formatCents(cheque.amountCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-end justify-between border-t border-slate-300 pt-3 text-xs">
        <div>
          <p className="font-semibold">
            {t('collection.collected')}: {formatCents(receipt.totalCents)} LKR
          </p>
          {receipt.notes && <p className="mt-1 text-slate-600">{receipt.notes}</p>}
        </div>
        <div className="text-center">
          <div className="mb-1 h-8 w-40 border-b border-slate-400" />
          <p>{t('report.sheet.collectedBy')}</p>
        </div>
      </div>
    </div>
  );
}
